import React from 'react';
import RadioButton from 'components/Inputs/RadioButton';
import { Column, NumberMenu, ColumnType, Table, TableProps } from '@spins/react-table';
import { EllipsisButton, Formats } from '@spins/react-ui-library';
import { campaignsDataType } from 'utils/types';
import { isValueDefined } from 'utils/functions';
import 'components/Campaigns/Tables/CampaignsTable/styles.scss';

interface CampaignsTableProps
  extends Pick<
    TableProps<any>,
    'error' | 'queryState' | 'rows' | 'totalRows' | 'showLoading' | 'onRowClick'
  > {
  selectedCampaign: string;
  columnsShown: Array<string>;
  onActionClick(key: string, id: string): void;
  onCampaignSelect(id: string): void;
}

const CampaignsTable: React.FC<CampaignsTableProps> = ({
  error,
  queryState,
  rows,
  showLoading,
  totalRows,
  selectedCampaign,
  columnsShown,
  onRowClick,
  onActionClick,
  onCampaignSelect,
}) => {
  const columns: Array<ColumnType<any>> = [
    {
      accessor: 'selectedId',
      cell: (value) => {
        return (
          <div onClick={(event) => event.stopPropagation()}>
            <RadioButton
              options={[{ id: value, label: '' }]}
              orientation="horizontal"
              value={selectedCampaign}
              onChange={onCampaignSelect}
            />
          </div>
        );
      },
      header: '',
      width: 3,
      isDraggable: false,
    },
    ...(columnsShown.includes('name')
      ? [
          {
            accessor: 'name',
            header: 'Name',
            width: 20,
            isDraggable: false,
            isSortable: true,
          },
        ]
      : []),
    ...(columnsShown.includes('objectives')
      ? [
          {
            accessor: 'objective',
            header: 'Campaign Objectives',
            width: 14,
            isDraggable: false,
          },
        ]
      : []),
    ...(columnsShown.includes('startDate')
      ? [
          {
            accessor: 'startDate',
            cell: (value: string) => (isValueDefined(value) ? Formats.date(value) : '-'),
            header: 'Start Date',
            width: 8,
            isDraggable: false,
          },
        ]
      : []),
    ...(columnsShown.includes('endDate')
      ? [
          {
            accessor: 'endDate',
            cell: (value: string) => (isValueDefined(value) ? Formats.date(value) : '-'),
            header: 'End Date',
            width: 8,
            isDraggable: false,
          },
        ]
      : []),
    ...(columnsShown.includes('accounts')
      ? [
          {
            accessor: 'storeFilters.counts.accounts',
            cell: (value: number, row: campaignsDataType) =>
              isValueDefined(row) ? Formats.number(row.storeFilters.counts.accounts) : '-',
            header: 'Accounts',
            width: 10,
            filter: {
              renderer: (column: Column<any>, onApply: any) => (
                <NumberMenu column={column} onApply={onApply} />
              ),
              props: { decimals: 0 },
            },
            isDraggable: false,
          },
        ]
      : []),
    ...(columnsShown.includes('stores')
      ? [
          {
            accessor: 'storeFilters.counts.stores',
            cell: (value: number, row: campaignsDataType) =>
              isValueDefined(row) ? Formats.number(row.storeFilters.counts.stores) : '-',
            header: 'Stores',
            width: 10,
            filter: {
              renderer: (column: Column<any>, onApply: any) => (
                <NumberMenu column={column} onApply={onApply} />
              ),
              props: { decimals: 0 },
            },
            isDraggable: false,
          },
        ]
      : []),
    ...(columnsShown.includes('items')
      ? [
          {
            accessor: 'itemFilters.counts.items',
            cell: (value: number, row: campaignsDataType) =>
              isValueDefined(row) ? Formats.number(row.itemFilters.counts.items) : '-',
            header: 'Items',
            width: 10,
            filter: {
              renderer: (column: Column<any>, onApply: any) => (
                <NumberMenu column={column} onApply={onApply} />
              ),
              props: { decimals: 0 },
            },
            isDraggable: false,
          },
        ]
      : []),
    {
      accessor: 'status',
      cell: (value) => (
        <div className="status">
          <span className={'status-' + value} />
          <span>{value}</span>
        </div>
      ),
      header: '',
      width: 10,
      isDraggable: false,
    },
    {
      accessor: 'id',
      cell: (value) => (
        <div onClick={(event) => event.stopPropagation()}>
          <EllipsisButton
            overlayPlacement="left"
            options={[
              { label: 'View', onClick: () => onActionClick('View', value) },
              { label: 'Archive', onClick: () => onActionClick('Archive', value) },
              { label: 'Edit', onClick: () => onActionClick('Edit', value) },
            ]}
          />
        </div>
      ),
      header: '',
      width: 3.5,
      isDraggable: false,
    },
  ];

  return (
    <Table
      key={selectedCampaign}
      columns={columns}
      error={error}
      layoutFixed={true}
      queryState={queryState}
      showLoading={showLoading}
      rows={rows}
      totalRows={totalRows}
      onRowClick={onRowClick}
    />
  );
};

export default CampaignsTable;
